import React, { useRef, useEffect, useState } from "react";
import { useChat } from "@/hooks/useChat";
import { useNodeExport } from "@/hooks/useNodeExport";
import MessageList from "@/components/chat/MessageList";
import MessageInput from "@/components/chat/MessageInput";
import ChatTranscriptPrint from "@/components/chat/ChatTranscriptPrint";
import SavedPrompts from "@/components/SavedPrompts";
import { MessageSquare } from "lucide-react";

const SUGGESTIONS = [
  "Which rep has the most stalled deals?",
  "What's my weighted forecast and how confident should I be?",
  "Where is my biggest pipeline risk right now?",
  "How is my win rate trending and what should I fix?",
];

export default function Chat({ onIncludedChange, onRegisterExport }) {
  const { messages, loading, send, toggleInclude, remove } = useChat(onIncludedChange);
  const [input, setInput] = useState("");
  const printRef = useRef(null);
  const savedPromptsRef = useRef(null);
  const answered = messages.filter((m) => m.answer);
  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => answered.length > 0,
    "salestap-analyst-transcript"
  );

  // Register the transcript exporter so the header PDF/Print act on this tab
  // when it is active. Re-register when the answered-message set changes.
  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("chat", { exportPdf, printFn: printNode });
  }, [onRegisterExport, exportPdf, printNode, answered.length]);

  return (
    <div>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Query Data</h2>
            <p className="text-xs text-muted-foreground">
              Questions answered from your synced CRM data. Toggle any answer to include it in the final
              report. Use the header PDF/Print to export this Q&amp;A transcript.
            </p>
          </div>
        </div>

        {/* Saved prompts — click to fill the box; save/edit/delete your own. */}
        <SavedPrompts ref={savedPromptsRef} onFill={(text) => setInput(text)} />

        {messages.length === 0 && !loading && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-xs border border-border rounded-full px-3 py-1.5 text-foreground/80 hover:bg-muted transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <MessageList
          messages={messages}
          loading={loading}
          onToggleInclude={toggleInclude}
          onRemove={remove}
        />
        <MessageInput
          value={input}
          onChange={setInput}
          onSend={send}
          onSaveDraft={(text) => savedPromptsRef.current?.savePrompt(text)}
          loading={loading}
        />
      </div>

      {/* Off-screen transcript layout for header PDF/Print on this tab */}
      <ChatTranscriptPrint ref={printRef} messages={messages} />
    </div>
  );
}
